import * as React from "react";
import { Box } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPActionsheetModal from "shared/components/shared/CPActionsheetModal/CPActionsheetModal";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import CPEmailTextBox from "shared/components/shared/CPEmailTextBox/CPEmailTextBox";
import CPTextArea from "shared/components/shared/CPTextArea/CPTextArea";
import CPAutoComplete from "shared/components/shared/CPAutoComplete/CPAutoComplete";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import { useCreateUserInviteMutation } from "data/users/userInvites";
import useAppState from "shared/state/useAppState";

interface AgencyOption {
  label: string;
  value: string;
}

const roleOptions = [
  { label: "CorpUser", value: "CorpUser" },
  { label: "Driver", value: "Driver" },
  { label: "Agency Manager / Owner", value: "Agency Manager / Owner" },
];

interface InviteFormState {
  firstname: string;
  lastname: string;
  email: string;
  note: string;
  agencyCode: string;
  roleName: string;
}

const emptyForm: InviteFormState = {
  firstname: "",
  lastname: "",
  email: "",
  note: "",
  agencyCode: "",
  roleName: "",
};

interface InviteUserPanelProps {
  open: boolean;
  agencyOptions: AgencyOption[];
  defaultAgencyCode?: string;
  onClose: () => void;
  onSent: () => void;
}

const InviteUserPanel: React.FC<InviteUserPanelProps> = ({
  open,
  agencyOptions,
  defaultAgencyCode,
  onClose,
  onSent,
}) => {
  const { setToastMsg, setIsLoading } = useAppState();
  const { mutate, error, isLoading, reset } =
    useCreateUserInviteMutation("admin");
  const [form, setForm] = React.useState<InviteFormState>(emptyForm);

  const selectedAgency = React.useMemo(
    () => agencyOptions.find((a) => a.value === form.agencyCode),
    [agencyOptions, form.agencyCode],
  );

  React.useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading]);

  React.useEffect(() => {
    if (open) {
      reset();
      setForm({ ...emptyForm, agencyCode: defaultAgencyCode ?? "" });
    }
  }, [open, defaultAgencyCode]);

  const isValid =
    form.firstname.trim() !== "" &&
    form.lastname.trim() !== "" &&
    form.email.trim() !== "" &&
    form.agencyCode !== "" &&
    form.roleName !== "";

  const handleSend = () => {
    if (!isValid) return;

    mutate(
      {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim(),
        note: form.note.trim() || null,
        agencyCode: form.agencyCode,
        roleName: form.roleName,
      },
      {
        onSuccess: () => {
          setToastMsg("Invite sent successfully", "success");
          onSent();
        },
        onError: (err: unknown) => {
          if ((err as Record<string, unknown>)["errors"]) {
            setToastMsg(
              "Please check the form for errors and try again",
              "error",
            );
            return;
          }
          setToastMsg("An error occurred while sending the invite.", "error");
        },
      },
    );
  };

  return (
    <CPActionsheetModal
      title="Invite User"
      open={open}
      onClose={onClose}
      position="right"
      width={500}
      actionBtns={
        <CPButton themeColor="primary" disabled={!isValid} onClick={handleSend}>
          Send Invite
        </CPButton>
      }
    >
      <Box spacing={24} flexDirection="column" style={{ paddingBottom: 16 }}>
        <Box spacing={12} flexDirection="column">
          <Box
            flexDirection="row"
            alignItems="flex-start"
            spacing={12}
            className="cp-actionsheet-input-box"
          >
            <CPTextBox
              name="firstname"
              label="First Name"
              required
              value={form.firstname}
              error={error}
              onChange={(e) =>
                setForm({
                  ...form,
                  firstname: e.target.value?.toString() || "",
                })
              }
            />
            <CPTextBox
              name="lastname"
              label="Last Name"
              required
              value={form.lastname}
              error={error}
              onChange={(e) =>
                setForm({
                  ...form,
                  lastname: e.target.value?.toString() || "",
                })
              }
            />
          </Box>

          <Box className="cp-actionsheet-input-box">
            <CPEmailTextBox
              name="email"
              label="Email Address"
              required
              value={form.email}
              error={error}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value?.toString() || "" })
              }
            />
          </Box>

          <Box className="cp-actionsheet-input-box">
            <CPAutoComplete
              name="agencyCode"
              label="Agency"
              required
              data={agencyOptions}
              allowManualEntry={false}
              value={selectedAgency}
              error={error}
              onChange={(item) =>
                setForm({ ...form, agencyCode: (item?.value as string) || "" })
              }
            />
          </Box>

          <Box className="cp-actionsheet-input-box">
            <CPDropDownList
              name="roleName"
              label="Role"
              required
              data={roleOptions}
              textField="label"
              dataItemKey="value"
              valueKey="value"
              value={form.roleName || null}
              error={error}
              onChange={(e) =>
                setForm({ ...form, roleName: (e.value as string) || "" })
              }
            />
          </Box>

          <Box className="cp-actionsheet-input-box">
            <CPTextArea
              name="note"
              label="Note (optional)"
              rows={4}
              maxLength={500}
              value={form.note}
              helpText="Include a personal message in the invite email."
              onChange={(e) => setForm({ ...form, note: e.value })}
            />
          </Box>
        </Box>
      </Box>
    </CPActionsheetModal>
  );
};

export default InviteUserPanel;
